import {
  AuthResponse,
  Category,
  Comment,
  CommentResponse,
  CommentSort,
  ImportJob,
  ImportQueueRequest,
  ImportVideoInfo,
  Playlist,
  RegisterRequest,
  UpdateVideoRequest,
  UserProfile,
  Video,
  VideoResponse,
  VideoUserInfo,
} from './types';
import axiosInstance from './services/axios';

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const API_URL = isLocalhost
  ? 'http://localhost:8111/api/v1'
  : 'https://api.gotube.cc/api/v1';

export { API_URL };

export const api = {
  videos: {
    getNew: async (params?: {
      page?: number;
      limit?: number;
      category_id?: string;
    }): Promise<VideoResponse> => {
      const response = await axiosInstance.get('/videos/new', { params });
      return response.data;
    },

    getPopular: async (params?: {
      page?: number;
      limit?: number;
      category_id?: string;
    }): Promise<VideoResponse> => {
      const response = await axiosInstance.get('/videos/popular', { params });
      return response.data;
    },

    getOne: async (code: string): Promise<Video> => {
      const response = await axiosInstance.get(`/videos/${code}`);
      return response.data;
    },

    recordView: async (code: string): Promise<void> => {
      await axiosInstance.post(`/videos/${code}/view`).catch(() => {});
    },

    upload: async (file: File): Promise<Video> => {
      const formData = new FormData();
      formData.append('video', file);

      const response = await axiosInstance.post('/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data;
    },

    update: async (code: string, data: UpdateVideoRequest): Promise<Video> => {
      const response = await axiosInstance.put(`/videos/${code}`, data);
      return response.data;
    },

    delete: async (code: string): Promise<void> => {
      await axiosInstance.delete(`/videos/${code}`);
    },

    like: async (videoId: string): Promise<void> => {
      await axiosInstance.post(`/videos/${videoId}/like`);
    },

    dislike: async (videoId: string): Promise<void> => {
      await axiosInstance.post(`/videos/${videoId}/dislike`);
    },

    getOneUserInfo: async (code: string): Promise<VideoUserInfo> => {
      const response = await axiosInstance.get(`/videos/user/${code}`);
      return response.data;
    },

    getUserVideos: async (
      userId: string,
      params?: { page?: number; limit?: number }
    ): Promise<VideoResponse> => {
      const response = await axiosInstance.get(`/users/${userId}/videos`, {
        params,
      });
      return response.data;
    },
  },

  comments: {
    getForVideo: async (
      videoCode: string,
      params?: { page?: number; limit?: number; sort?: CommentSort }
    ): Promise<CommentResponse> => {
      const response = await axiosInstance.get(
        `/videos/${videoCode}/comments`,
        { params }
      );
      return response.data;
    },

    add: async (
      videoCode: string,
      text: string,
      parentId?: string
    ): Promise<Comment> => {
      const response = await axiosInstance.post(
        `/videos/${videoCode}/comments`,
        {
          text,
          parent_id: parentId || null,
        }
      );
      return response.data;
    },

    delete: async (id: string): Promise<void> => {
      await axiosInstance.delete(`/comments/${id}`);
    },

    like: async (id: string): Promise<void> => {
      await axiosInstance.post(`/comments/${id}/like`);
    },

    dislike: async (id: string): Promise<void> => {
      await axiosInstance.post(`/comments/${id}/dislike`);
    },
  },

  categories: {
    getAll: async (): Promise<Category[]> => {
      const response = await axiosInstance.get('/categories');
      return response.data;
    },
  },

  users: {
    getMe: async (): Promise<UserProfile> => {
      const response = await axiosInstance.get('/users/me');
      return response.data;
    },

    getById: async (id: string): Promise<UserProfile> => {
      const response = await axiosInstance.get(`/users/${id}`);
      return response.data;
    },

    updateMe: async (data: {
      username?: string;
      description?: string;
    }): Promise<UserProfile> => {
      const response = await axiosInstance.put('/users/me', data);
      return response.data;
    },

    uploadAvatar: async (file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);
      const response = await axiosInstance.post('/users/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    uploadBanner: async (file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);
      const response = await axiosInstance.post('/users/me/banner', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    changePassword: async (data: {
      current_password: string;
      new_password: string;
    }): Promise<void> => {
      await axiosInstance.put('/users/me/password', data);
    },
  },

  import: {
    getInfo: async (url: string): Promise<ImportVideoInfo> => {
      const response = await axiosInstance.post('/import/info', { url });
      return response.data;
    },

    addToQueue: async (data: ImportQueueRequest): Promise<ImportJob> => {
      const response = await axiosInstance.post('/import/queue', data);
      return response.data;
    },

    getJobs: async (): Promise<ImportJob[]> => {
      const response = await axiosInstance.get('/import/jobs');
      return response.data;
    },
  },

  playlists: {
    getForUser: async (userId: string): Promise<Playlist[]> => {
      const response = await axiosInstance.get(`/users/${userId}/playlists`);
      return response.data;
    },

    getMine: async (videoId?: string): Promise<Playlist[]> => {
      const response = await axiosInstance.get('/users/me/playlists', {
        params: videoId ? { video_id: videoId } : undefined,
      });
      return response.data;
    },

    getOne: async (id: string): Promise<Playlist> => {
      const response = await axiosInstance.get(`/playlists/${id}`);
      return response.data;
    },

    create: async (data: {
      title: string;
      description?: string;
    }): Promise<Playlist> => {
      const response = await axiosInstance.post('/playlists', data);
      return response.data;
    },

    update: async (
      id: string,
      data: { title: string; description?: string }
    ): Promise<Playlist> => {
      const response = await axiosInstance.put(`/playlists/${id}`, data);
      return response.data;
    },

    remove: async (id: string): Promise<void> => {
      await axiosInstance.delete(`/playlists/${id}`);
    },

    addVideo: async (id: string, videoId: string): Promise<void> => {
      await axiosInstance.post(`/playlists/${id}/videos`, { video_id: videoId });
    },

    removeVideo: async (id: string, videoId: string): Promise<void> => {
      await axiosInstance.delete(`/playlists/${id}/videos/${videoId}`);
    },
  },

  auth: {
    login: async (email: string, password: string): Promise<AuthResponse> => {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password,
      });
      return response.data;
    },

    register: async (data: RegisterRequest): Promise<AuthResponse> => {
      const response = await axiosInstance.post('/auth/register', data);
      return response.data;
    },

    refresh: async (refreshToken: string): Promise<AuthResponse> => {
      const response = await axiosInstance.post('/auth/refresh', {
        refresh_token: refreshToken,
      });
      return response.data;
    },
  },
};
